import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';

/**
 * Header mega-menu: open on click; close on outside click, empty panel click, or Escape.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {HTMLElement | null} */
  #activeItem = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#onResize);

    this.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  #onResize = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.menu-list__link');

    // Empty mega-menu chrome (padding / panel background) still sits inside
    // header-menu, so document outside-click never fires — close here.
    if (!(trigger instanceof HTMLElement)) {
      if (
        this.#activeItem &&
        !event.target.closest('a, button, input, textarea, select, label, summary')
      ) {
        this.#close();
      }
      return;
    }

    // Clicks on links inside the mega menu should navigate normally.
    if (trigger.closest('.menu-list__submenu')) return;

    const listItem = trigger.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement) || !this.contains(listItem)) return;

    if (!hasSubmenu(trigger, listItem)) {
      this.#close();
      return;
    }

    event.preventDefault();

    if (this.#isOpenFor(listItem)) {
      this.#close();
      return;
    }

    this.#open(listItem);
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;

    this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#activeItem) return;

    this.#close();
  };

  /**
   * @param {HTMLElement} listItem
   * @returns {boolean}
   */
  #isOpenFor(listItem) {
    if (!this.#activeItem) return false;

    if (listItem.slot === 'more') {
      return this.dataset.overflowExpanded === 'true';
    }

    return menuLinkFor(listItem) === this.#activeItem;
  }

  /**
   * @param {HTMLElement} listItem
   */
  #open(listItem) {
    const triggerLink = menuLinkFor(listItem);
    if (!triggerLink) return;

    // "More" opens the first overflow item.
    const targetListItem =
      listItem.slot === 'more' ? this.querySelector('[slot="overflow"]') : listItem;
    const targetLink =
      targetListItem instanceof HTMLElement ? menuLinkFor(targetListItem) : triggerLink;

    if (!targetLink) return;

    if (targetLink !== this.#activeItem) {
      if (this.#activeItem) {
        this.#activeItem.setAttribute('aria-expanded', 'false');
        findSubmenu(this.#activeItem)?.removeAttribute('data-active');
      }

      this.#activeItem = targetLink;
      this.setAttribute('aria-expanded', 'true');
      this.dataset.overflowExpanded = listItem.slot !== '' ? 'true' : 'false';

      if (targetLink.hasAttribute('aria-haspopup') || targetLink.hasAttribute('aria-expanded')) {
        targetLink.setAttribute('aria-expanded', 'true');
      }

      const submenu = findSubmenu(targetLink);
      if (submenu) submenu.dataset.active = '';

      if (listItem.slot === 'more') {
        triggerLink.setAttribute('aria-expanded', 'true');
      }
    }
  }

  #close() {
    if (!this.#activeItem) return;

    findSubmenu(this.#activeItem)?.removeAttribute('data-active');
    this.#activeItem.setAttribute('aria-expanded', 'false');

    const moreLink = this.querySelector('[slot="more"] .menu-list__link');
    moreLink?.setAttribute('aria-expanded', 'false');

    this.#activeItem = null;
    this.setAttribute('aria-expanded', 'false');
    this.dataset.overflowExpanded = 'false';
  }

  #preloadImages = () => {
    for (const image of this.querySelectorAll('img[loading="lazy"]')) {
      image.removeAttribute('loading');
    }
  };

  // Back-compat for any leftover declarative handlers
  /** @param {Event} event */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;
    const listItem = event.target.closest('.menu-list__list-item');
    if (listItem instanceof HTMLElement) this.#open(listItem);
  };

  deactivate = () => {
    this.#close();
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {HTMLElement} trigger
 * @param {HTMLElement} listItem
 * @returns {boolean}
 */
function hasSubmenu(trigger, listItem) {
  return (
    trigger.hasAttribute('aria-haspopup') ||
    listItem.slot === 'more' ||
    Boolean(listItem.querySelector(':scope > .menu-list__submenu'))
  );
}

/**
 * @param {HTMLElement} listItem
 * @returns {HTMLElement | null}
 */
function menuLinkFor(listItem) {
  const link = listItem.querySelector('[ref="menuitem"], .menu-list__link');
  return link instanceof HTMLElement ? link : null;
}

/**
 * @param {Element | null | undefined} menuitem
 * @returns {HTMLElement | null}
 */
function findSubmenu(menuitem) {
  const submenu = menuitem?.parentElement?.querySelector(
    '[ref="submenu[]"], .menu-list__submenu',
  );
  return submenu instanceof HTMLElement ? submenu : null;
}
